import { useState } from "react";
import { Search, FileText, ChevronRight, Loader2, Star } from "lucide-react";
import { useHelpContent } from "./useHelpContent";

interface HelpArticleListProps {
	app: string;
	currentRoute: string;
	onSelectArticle: (slug: string) => void;
}

interface ArticleButtonProps {
	slug: string;
	title: string;
	description?: string;
	onSelect: (slug: string) => void;
}

const ArticleButton: React.FC<ArticleButtonProps> = ({
	slug,
	title,
	description,
	onSelect,
}) => (
	<button
		onClick={() => onSelect(slug)}
		className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-md hover:bg-gray-50 transition-colors group"
		data-testid={`help-article-${slug}`}
	>
		<FileText className="h-4 w-4 text-gray-400 shrink-0" />
		<div className="flex-1 min-w-0">
			<p className="text-sm text-gray-700 truncate">{title}</p>
			{description && (
				<p className="text-xs text-gray-400 truncate">{description}</p>
			)}
		</div>
		<ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-500" />
	</button>
);

export const HelpArticleList: React.FC<HelpArticleListProps> = ({
	app,
	currentRoute,
	onSelectArticle,
}) => {
	const { loading, getArticlesForRoute, searchArticles } = useHelpContent();
	const [query, setQuery] = useState("");

	const searchResults = query ? searchArticles(query, app) : null;
	const { recommended, other } = getArticlesForRoute(currentRoute, app);

	if (loading) {
		return (
			<div className="flex items-center justify-center py-8">
				<Loader2 className="h-5 w-5 animate-spin text-gray-400" />
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<div className="relative">
				<Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
				<input
					type="text"
					placeholder="Artikel suchen..."
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
					aria-label="Hilfe-Artikel suchen"
					data-testid="help-search-input"
				/>
			</div>

			<div className="space-y-1">
				{searchResults ? (
					<>
						{searchResults.map((article) => (
							<ArticleButton
								key={article.slug}
								slug={article.slug}
								title={article.title}
								description={article.description}
								onSelect={onSelectArticle}
							/>
						))}
						{searchResults.length === 0 && (
							<p className="text-xs text-gray-400 text-center py-4">
								Keine Artikel gefunden
							</p>
						)}
					</>
				) : (
					<>
						{recommended.length > 0 && (
							<>
								<div className="flex items-center gap-1.5 px-3 pt-1 pb-0.5">
									<Star className="h-3 w-3 text-amber-500" />
									<span className="text-xs font-medium text-gray-500">
										Hilfeartikel für diese Seite
									</span>
								</div>
								{recommended.map((article) => (
									<ArticleButton
										key={article.slug}
										slug={article.slug}
										title={article.title}
										description={article.description}
										onSelect={onSelectArticle}
									/>
								))}
								<div className="px-3 pt-3 pb-0.5">
									<span className="text-xs font-medium text-gray-500">
										Alle Artikel
									</span>
								</div>
							</>
						)}
						{other.map((article) => (
							<ArticleButton
								key={article.slug}
								slug={article.slug}
								title={article.title}
								description={article.description}
								onSelect={onSelectArticle}
							/>
						))}
					</>
				)}
			</div>
		</div>
	);
};
